import { BigNumber } from './big-number';
import { formatCardPercent, formatCount } from './format';
import { Legend } from './legend';

interface SplitBarProps {
  left: { label: string; value: number };
  right: { label: string; value: number };
  /** Label each part with its share instead of its count. */
  showShare?: boolean;
}

/** Narrowest the card's bar gets (half-width card on a small laptop), and roughly how wide one 13px character is. */
const MIN_WIDTH = 340;
const CHAR = 8;

export function SplitBar({ left, right, showShare = false }: SplitBarProps) {
  const total = left.value + right.value;
  if (total === 0) return <BigNumber value='—' className='mt-auto pt-6' />;
  const leftShare = (left.value / total) * 100;
  const text = (value: number): string => (showShare ? formatCardPercent(value / total) : formatCount(value));
  // The right number sits over its own segment, or at the bar's end when the left one would run into it.
  const roomy = (leftShare / 100) * MIN_WIDTH >= text(left.value).length * CHAR + 12;
  return (
    <div className='mt-2 flex flex-col gap-1.5' aria-label={`${left.label} ${formatCount(left.value)}, ${right.label} ${formatCount(right.value)}`}>
      <div className='flex justify-between gap-1.5 text-[13px] font-medium leading-[18px] tabular-nums text-ink'>
        <span className='min-w-fit' style={roomy ? { width: `${leftShare}%` } : undefined}>{text(left.value)}</span>
        <span className={roomy ? 'flex-1' : undefined}>{text(right.value)}</span>
      </div>
      <div className='flex h-4 gap-1.5 rounded-full bg-gray-300 p-0.5'>
        {left.value > 0 ? <div className='rounded-[6px] bg-indigo-700' style={{ width: `${leftShare}%` }} /> : null}
        {right.value > 0 ? <div className='flex-1 rounded-[6px] bg-indigo-300' /> : null}
      </div>
      <Legend items={[{ label: left.label, swatch: 'bg-indigo-700' }, { label: right.label, swatch: 'bg-indigo-200' }]} />
    </div>
  );
}
